import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { kioskApi, api } from '../lib/api';
import { toast } from 'sonner';
import {
  Clock, LogIn, Coffee, Undo2, LogOut, Delete, ArrowLeft,
  CheckCircle2, ShieldCheck, Lock, Building2, Bell, Check, X, Search, FileClock, Megaphone, KeyRound, PartyPopper, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ESTADO_LABEL, ESTADO_DOT, ACCION_CONFIG, formatHoras } from '../lib/labels';

const ICONS = { LogIn, Coffee, Undo2, LogOut };
const TIPO_FESTIVO_LABEL = { nacional: 'Festivo nacional', autonomico: 'Festivo autonómico', local: 'Festivo local', empresa: 'Festivo de empresa' };

const Reloj = () => {
  const [ahora, setAhora] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-center">
      <div className="font-heading text-5xl sm:text-7xl font-bold tracking-tight tabular-nums text-zinc-900" data-testid="kiosk-clock">
        {ahora.toLocaleTimeString('es-ES')}
      </div>
      <div className="text-zinc-500 text-base sm:text-lg capitalize mt-1">
        {ahora.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </div>
    </div>
  );
};

// ===== Pantalla de activación de la tablet =====
const Activacion = ({ onActivar }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const activar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/kiosk/activate', { username, password });
      localStorage.setItem('kiosk_token', res.data.kiosk_token);
      localStorage.setItem('kiosk_empresa', res.data.empresa_nombre);
      localStorage.setItem('kiosk_color', res.data.color || '#18181B');
      localStorage.setItem('kiosk_logo', res.data.logo_url || '');
      onActivar(res.data.empresa_nombre);
      toast.success(`Tablet activada para ${res.data.empresa_nombre}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo activar la tablet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-zinc-200 shadow-lg">
        <CardHeader className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="bg-zinc-900 p-3 rounded-2xl"><Lock className="h-7 w-7 text-white" /></div>
          </div>
          <CardTitle className="font-heading text-2xl font-bold text-zinc-900">Activar Tablet de Fichaje</CardTitle>
          <p className="text-zinc-500 text-sm">Un administrador debe iniciar sesión para habilitar el fichaje de sus empleados en este dispositivo.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={activar} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="k-user">Usuario administrador</Label>
              <Input id="k-user" data-testid="kiosk-activate-username" value={username}
                onChange={(e) => setUsername(e.target.value)} required autoComplete="username" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="k-pass">Contraseña</Label>
              <Input id="k-pass" type="password" data-testid="kiosk-activate-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="kiosk-activate-btn">
              {loading ? 'Activando...' : 'Activar tablet'}
            </Button>
          </form>
          <Link to="/admin/login" className="flex items-center justify-center gap-2 text-zinc-500 hover:text-zinc-900 text-sm mt-5">
            <ArrowLeft className="h-4 w-4" /> Ir al panel de administración
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

const Kiosk = () => {
  const [activado, setActivado] = useState(!!localStorage.getItem('kiosk_token'));
  const [empresaNombre, setEmpresaNombre] = useState(localStorage.getItem('kiosk_empresa') || '');
  const [color, setColor] = useState(localStorage.getItem('kiosk_color') || '#18181B');
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('kiosk_logo') || '');
  const logo = logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : '';
  const [pantalla, setPantalla] = useState('grid');
  const [empleados, setEmpleados] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [pin, setPin] = useState('');
  const [estado, setEstado] = useState(null);
  const [acciones, setAcciones] = useState([]);
  const [eventosHoy, setEventosHoy] = useState([]);
  const [festivoHoy, setFestivoHoy] = useState(null);
  const [cierresAuto, setCierresAuto] = useState([]);
  const [cambios, setCambios] = useState([]);
  const [comunicados, setComunicados] = useState([]);
  const [pinForm, setPinForm] = useState({ actual: '', nuevo: '', repetir: '' });
  const [misFichajes, setMisFichajes] = useState(null);
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [exito, setExito] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(true);
  const timeoutRef = useRef(null);

  const cargarEmpleados = useCallback(async () => {
    if (!localStorage.getItem('kiosk_token')) return;
    try {
      const res = await kioskApi.get('/kiosk/empleados');
      setEmpleados(res.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem('kiosk_token');
        localStorage.removeItem('kiosk_empresa');
        setActivado(false);
        toast.error('La sesión de la tablet ha caducado. Vuelve a activarla.');
      }
    } finally {
      setCargandoLista(false);
    }
  }, []);

  useEffect(() => { if (activado) cargarEmpleados(); }, [activado, cargarEmpleados]);

  const volverGrid = useCallback(() => {
    setPantalla('grid'); setSeleccionado(null); setPin('');
    setEstado(null); setAcciones([]); setEventosHoy([]); setFestivoHoy(null); setCierresAuto([]); setExito(null);
    setCambios([]); setMisFichajes(null); setComunicados([]); setPinForm({ actual: '', nuevo: '', repetir: '' });
    cargarEmpleados();
  }, [cargarEmpleados]);

  useEffect(() => {
    if (['pin', 'actions', 'cambios', 'mis_fichajes', 'comunicados', 'cambiar_pin'].includes(pantalla)) {
      timeoutRef.current = setTimeout(volverGrid, 30000);
      return () => clearTimeout(timeoutRef.current);
    }
  }, [pantalla, pin, volverGrid]);

  const bloquearTablet = () => {
    localStorage.removeItem('kiosk_token');
    localStorage.removeItem('kiosk_empresa');
    localStorage.removeItem('kiosk_color');
    localStorage.removeItem('kiosk_logo');
    setActivado(false);
    setEmpresaNombre('');
    toast.success('Tablet bloqueada');
  };

  const seleccionarEmpleado = (emp) => { setSeleccionado(emp); setPin(''); setPantalla('pin'); };
  const pulsarDigito = (d) => { if (pin.length < 6) setPin(pin + d); };
  const borrar = () => setPin(pin.slice(0, -1));

  const confirmarPin = async () => {
    if (pin.length < 4) { toast.error('El PIN debe tener al menos 4 dígitos'); return; }
    setCargando(true);
    try {
      const res = await kioskApi.post('/kiosk/verify-pin', { user_id: seleccionado.id, pin });
      setEstado(res.data.estado);
      setAcciones(res.data.acciones);
      setEventosHoy(res.data.eventos_hoy);
      setFestivoHoy(res.data.festivo_hoy || null);
      const cierresPend = res.data.cierres_automaticos || [];
      setCierresAuto(cierresPend);
      setCambios(res.data.cambios_pendientes || []);
      setComunicados(res.data.comunicados_pendientes || []);
      if (cierresPend.length > 0) setPantalla('cierres_auto_ack');
      else if ((res.data.cambios_pendientes || []).length > 0) setPantalla('cambios');
      else if ((res.data.comunicados_pendientes || []).length > 0) setPantalla('comunicados');
      else setPantalla('actions');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PIN incorrecto');
      setPin('');
    } finally { setCargando(false); }
  };

  const responderAutoCierre = async (cierreId, aceptar, comentario) => {
    try {
      await kioskApi.post('/kiosk/auto-cierre/responder', {
        user_id: seleccionado.id, pin, cierre_id: cierreId, aceptar, comentario,
      });
      const restantes = cierresAuto.filter((c) => c.id !== cierreId);
      setCierresAuto(restantes);
      toast.success(aceptar ? 'Cierre confirmado' : 'Rechazo enviado');
      if (restantes.length === 0) {
        if (cambios.length > 0) setPantalla('cambios');
        else if (comunicados.length > 0) setPantalla('comunicados');
        else setPantalla('actions');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar tu respuesta');
    }
  };

  const aceptarComunicado = async (comId) => {
    try {
      await kioskApi.post('/kiosk/comunicado/aceptar', { user_id: seleccionado.id, pin, comunicado_id: comId });
      const restantes = comunicados.filter((c) => c.id !== comId);
      setComunicados(restantes);
      toast.success('Comunicado aceptado');
      if (restantes.length === 0) setPantalla('actions');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aceptar');
    }
  };

  const cambiarPin = async () => {
    if (pinForm.nuevo.length < 4) { toast.error('El nuevo PIN debe tener al menos 4 dígitos'); return; }
    if (pinForm.nuevo !== pinForm.repetir) { toast.error('El nuevo PIN no coincide'); return; }
    try {
      await kioskApi.post('/kiosk/cambiar-pin', { user_id: seleccionado.id, pin_actual: pinForm.actual, pin_nuevo: pinForm.nuevo });
      toast.success('PIN cambiado correctamente');
      volverGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el PIN');
    }
  };

  const validarCambio = async (cambioId, aceptar) => {
    let comentario = '';
    if (!aceptar) {
      comentario = window.prompt('Motivo del rechazo (opcional):', '') || '';
    }
    try {
      await kioskApi.post('/kiosk/validar-cambio', { user_id: seleccionado.id, pin, cambio_id: cambioId, aceptar, comentario });
      const restantes = cambios.filter((c) => c.id !== cambioId);
      setCambios(restantes);
      toast.success(aceptar ? 'Cambio aceptado' : 'Cambio rechazado');
      if (restantes.length === 0) setPantalla('actions');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al validar');
    }
  };

  const buscarMisFichajes = async () => {
    try {
      const res = await kioskApi.post('/kiosk/mis-fichajes', {
        user_id: seleccionado.id, pin, desde: rango.desde || null, hasta: rango.hasta || null,
      });
      setMisFichajes(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al consultar');
    }
  };

  const fichar = async (tipo) => {
    setCargando(true);
    try {
      const res = await kioskApi.post('/kiosk/fichar', { user_id: seleccionado.id, pin, tipo });
      setExito({ accion: ACCION_CONFIG[tipo].label, hora: res.data.hora, nombre: res.data.full_name });
      setPantalla('success');
      setTimeout(volverGrid, 3500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al fichar');
    } finally { setCargando(false); }
  };

  if (!activado) {
    return <Activacion onActivar={(nombre) => {
      setActivado(true);
      setEmpresaNombre(nombre);
      setColor(localStorage.getItem('kiosk_color') || '#18181B');
      setLogoUrl(localStorage.getItem('kiosk_logo') || '');
    }} />;
  }

  // ===== GRID =====
  if (pantalla === 'grid') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col p-6 sm:p-10">
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="logo" className="h-11 w-11 rounded-xl object-contain bg-white border border-zinc-200" />
            ) : (
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: color }}><Clock className="h-6 w-6 text-white" /></div>
            )}
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-zinc-900">{empresaNombre || 'Control Horario'}</h1>
              <p className="text-zinc-500 text-sm flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> Selecciona tu nombre para fichar
              </p>
            </div>
          </div>
          <button onClick={bloquearTablet} data-testid="lock-tablet-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-red-600 text-sm font-medium transition-colors">
            <Lock className="h-4 w-4" /> Bloquear tablet
          </button>
        </header>

        <div className="mb-10"><Reloj /></div>

        {cargandoLista ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400">Cargando empleados...</div>
        ) : empleados.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
            <ShieldCheck className="h-12 w-12 mb-3" />
            <p className="text-lg">No hay empleados registrados en esta empresa.</p>
            <p className="text-sm">El administrador debe crearlos desde el panel.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 max-w-6xl mx-auto w-full">
            {empleados.map((emp) => (
              <button key={emp.id} onClick={() => seleccionarEmpleado(emp)}
                data-testid={`employee-card-${emp.username}`}
                className="flex flex-col items-center justify-center p-6 bg-white border border-zinc-200 rounded-2xl shadow-sm active:scale-95 hover:border-zinc-400 transition-all min-h-[150px]">
                <div className="h-16 w-16 rounded-full text-white font-bold text-xl flex items-center justify-center mb-3" style={{ backgroundColor: color }}>
                  {emp.iniciales}
                </div>
                <span className="font-semibold text-zinc-900 text-center leading-tight">{emp.full_name}</span>
                <span className="flex items-center gap-1.5 mt-2 text-xs text-zinc-500">
                  <span className={`h-2 w-2 rounded-full ${ESTADO_DOT[emp.estado]}`} />
                  {ESTADO_LABEL[emp.estado]}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== PIN =====
  if (pantalla === 'pin') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center justify-center p-6">
        <button onClick={volverGrid} data-testid="pin-back-btn"
          className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="h-20 w-20 rounded-full text-white font-bold text-2xl flex items-center justify-center mb-4" style={{ backgroundColor: color }}>
          {seleccionado.iniciales}
        </div>
        <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-1">{seleccionado.full_name}</h2>
        <p className="text-zinc-500 mb-6">Introduce tu PIN</p>
        <div className="flex gap-3 mb-8" data-testid="pin-display">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`h-4 w-4 rounded-full border-2 ${i < pin.length ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300'}`} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button key={n} onClick={() => pulsarDigito(String(n))} data-testid={`pin-pad-${n}`}
              className="w-20 h-20 rounded-full bg-white border border-zinc-200 text-3xl font-bold text-zinc-900 shadow-sm active:bg-zinc-100 active:scale-90 transition-all">
              {n}
            </button>
          ))}
          <button onClick={borrar} data-testid="pin-pad-delete"
            className="w-20 h-20 rounded-full flex items-center justify-center text-zinc-500 active:scale-90 transition-all">
            <Delete className="h-7 w-7" />
          </button>
          <button onClick={() => pulsarDigito('0')} data-testid="pin-pad-0"
            className="w-20 h-20 rounded-full bg-white border border-zinc-200 text-3xl font-bold text-zinc-900 shadow-sm active:bg-zinc-100 active:scale-90 transition-all">
            0
          </button>
          <button onClick={confirmarPin} disabled={cargando || pin.length < 4} data-testid="pin-confirm-btn"
            className="w-20 h-20 rounded-full bg-zinc-900 text-white flex items-center justify-center active:scale-90 transition-all disabled:opacity-30">
            <CheckCircle2 className="h-8 w-8" />
          </button>
        </div>
      </div>
    );
  }

  // ===== ACTIONS =====
  if (pantalla === 'actions') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center justify-center p-6">
        <button onClick={volverGrid} data-testid="actions-back-btn"
          className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="text-center mb-8">
          <div className="h-20 w-20 rounded-full text-white font-bold text-2xl flex items-center justify-center mb-3 mx-auto" style={{ backgroundColor: color }}>
            {seleccionado.iniciales}
          </div>
          <h2 className="font-heading text-2xl font-bold text-zinc-900">{seleccionado.full_name}</h2>
          <span className="inline-flex items-center gap-2 mt-2 px-4 py-1.5 rounded-full text-sm font-bold border">
            <span className={`h-2.5 w-2.5 rounded-full ${ESTADO_DOT[estado]}`} /> {ESTADO_LABEL[estado]}
          </span>
        </div>
        <div className="w-full max-w-md space-y-4">
          {festivoHoy && (
            <div data-testid="kiosk-festivo-hoy"
              className="w-full flex items-center gap-3 rounded-xl border-2 border-red-300 bg-red-50 text-red-800 px-4 py-3">
              <PartyPopper className="h-6 w-6 shrink-0" />
              <div className="text-left">
                <p className="font-bold leading-tight">Hoy es festivo</p>
                <p className="text-sm leading-tight">{festivoHoy.nombre}{festivoHoy.tipo ? ` · ${TIPO_FESTIVO_LABEL[festivoHoy.tipo] || festivoHoy.tipo}` : ''}</p>
              </div>
            </div>
          )}
          {cambios.length > 0 && (
            <button onClick={() => setPantalla('cambios')} data-testid="review-cambios-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-800 font-semibold py-3">
              <Bell className="h-5 w-5" /> Tienes {cambios.length} cambio(s) por revisar
            </button>
          )}
          {comunicados.length > 0 && (
            <button onClick={() => setPantalla('comunicados')} data-testid="review-comunicados-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-blue-400 bg-blue-50 text-blue-800 font-semibold py-3">
              <Megaphone className="h-5 w-5" /> Tienes {comunicados.length} comunicado(s) por leer
            </button>
          )}
          {acciones.length === 0 && (
            <div className="text-center text-zinc-500 bg-white border border-zinc-200 rounded-2xl p-8" data-testid="no-actions-msg">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-red-500" />
              Tu jornada de hoy ya está finalizada. ¡Hasta mañana!
            </div>
          )}
          {acciones.map((accion) => {
            const cfg = ACCION_CONFIG[accion];
            const Icon = ICONS[cfg.icon];
            return (
              <button key={accion} onClick={() => fichar(accion)} disabled={cargando}
                data-testid={`btn-fichar-${accion}`}
                className={`w-full h-28 text-2xl font-bold rounded-2xl shadow-md flex items-center justify-center gap-3 border-2 transition-all active:scale-95 disabled:opacity-50 ${cfg.btn}`}>
                <Icon className="h-8 w-8" strokeWidth={2.5} /> {cfg.label}
              </button>
            );
          })}
          <button onClick={() => { setMisFichajes(null); setPantalla('mis_fichajes'); }} data-testid="ver-mis-fichajes-btn"
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white text-zinc-700 font-medium py-3 hover:bg-zinc-50">
            <FileClock className="h-5 w-5" /> Ver mis fichajes
          </button>
          <button onClick={() => { setPinForm({ actual: '', nuevo: '', repetir: '' }); setPantalla('cambiar_pin'); }} data-testid="cambiar-pin-btn"
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white text-zinc-700 font-medium py-3 hover:bg-zinc-50">
            <KeyRound className="h-5 w-5" /> Cambiar mi PIN
          </button>
        </div>
        {eventosHoy.length > 0 && (
          <div className="mt-8 w-full max-w-md bg-white border border-zinc-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Fichajes de hoy</p>
            <div className="space-y-1">
              {eventosHoy.map((ev, i) => (
                <div key={i} className="flex justify-between text-sm text-zinc-700">
                  <span>{ACCION_CONFIG[ev.tipo]?.label}</span>
                  <span className="tabular-nums">{new Date(ev.timestamp).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid' })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== CIERRES AUTOMÁTICOS: aceptar/rechazar =====
  if (pantalla === 'cierres_auto_ack') {
    return <KioskCierresAckScreen
      cierres={cierresAuto}
      seleccionado={seleccionado}
      color={color}
      onResponder={responderAutoCierre}
      onVolver={volverGrid}
    />;
  }

  // ===== CAMBIOS PENDIENTES =====
  if (pantalla === 'cambios') {
    const ACCION_TXT = { crear: 'Fichaje añadido', editar: 'Fichaje modificado', eliminar: 'Fichaje eliminado' };
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center p-6 sm:p-10" data-testid="cambios-screen">
        <button onClick={volverGrid} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="text-center mb-6 mt-6">
          <Bell className="h-10 w-10 mx-auto mb-2 text-amber-500" />
          <h2 className="font-heading text-2xl font-bold text-zinc-900">Cambios en tus fichajes</h2>
          <p className="text-zinc-500 text-sm max-w-md">La empresa ha realizado cambios en tu registro. Revísalos y acéptalos o recházalos.</p>
        </div>
        <div className="w-full max-w-lg space-y-3">
          {cambios.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`cambio-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-zinc-900">{ACCION_TXT[c.accion] || c.accion}</span>
                <span className="text-xs text-zinc-400 tabular-nums">{new Date(c.timestamp).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}</span>
              </div>
              <p className="text-sm text-zinc-600 mb-3">{c.detalle}</p>
              <div className="flex gap-2">
                <Button onClick={() => validarCambio(c.id, true)} data-testid={`aceptar-${c.id}`}
                  className="flex-1 bg-green-600 hover:bg-green-700"><Check className="h-4 w-4 mr-1" /> Aceptar</Button>
                <Button onClick={() => validarCambio(c.id, false)} data-testid={`rechazar-${c.id}`}
                  variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50"><X className="h-4 w-4 mr-1" /> Rechazar</Button>
              </div>
            </div>
          ))}
          {cambios.length === 0 && (
            <p className="text-center text-zinc-400">No hay cambios pendientes.</p>
          )}
          <Button onClick={() => setPantalla(comunicados.length > 0 ? 'comunicados' : 'actions')} variant="outline" className="w-full" data-testid="continuar-fichaje-btn">
            Continuar
          </Button>
        </div>
      </div>
    );
  }

  // ===== COMUNICADOS =====
  if (pantalla === 'comunicados') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center p-6 sm:p-10" data-testid="comunicados-screen">
        <button onClick={volverGrid} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="text-center mb-6 mt-6">
          <Megaphone className="h-10 w-10 mx-auto mb-2 text-blue-500" />
          <h2 className="font-heading text-2xl font-bold text-zinc-900">Comunicados de la empresa</h2>
          <p className="text-zinc-500 text-sm max-w-md">Lee los comunicados y confirma que los has leído. Puedes continuar y fichar en cualquier momento.</p>
        </div>
        <div className="w-full max-w-lg space-y-3">
          {comunicados.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`kiosk-comunicado-${c.id}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {c.urgente && <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">URGENTE</span>}
                <span className="font-semibold text-zinc-900">{c.titulo}</span>
              </div>
              <p className="text-sm text-zinc-600 mb-3 whitespace-pre-wrap">{c.cuerpo}</p>
              <Button onClick={() => aceptarComunicado(c.id)} data-testid={`aceptar-comunicado-${c.id}`}
                className="w-full bg-blue-600 hover:bg-blue-700"><Check className="h-4 w-4 mr-1" /> He leído y acepto</Button>
            </div>
          ))}
          {comunicados.length === 0 && <p className="text-center text-zinc-400">No hay comunicados pendientes.</p>}
          <Button onClick={() => setPantalla('actions')} variant="outline" className="w-full" data-testid="continuar-desde-comunicados-btn">
            Continuar al fichaje
          </Button>
        </div>
      </div>
    );
  }

  // ===== CAMBIAR PIN =====
  if (pantalla === 'cambiar_pin') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center justify-center p-6" data-testid="cambiar-pin-screen">
        <button onClick={() => setPantalla('actions')} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="w-full max-w-sm bg-white border border-zinc-200 rounded-2xl p-6 space-y-4">
          <div className="text-center">
            <KeyRound className="h-9 w-9 mx-auto mb-2 text-zinc-700" />
            <h2 className="font-heading text-xl font-bold text-zinc-900">Cambiar mi PIN</h2>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-zinc-600">PIN actual</label>
            <input type="password" inputMode="numeric" maxLength={6} value={pinForm.actual} data-testid="pin-actual-input"
              onChange={(e) => setPinForm({ ...pinForm, actual: e.target.value.replace(/\D/g, '') })}
              className="w-full text-center text-2xl tracking-widest border border-zinc-300 rounded-xl py-3" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-zinc-600">Nuevo PIN (4-6 dígitos)</label>
            <input type="password" inputMode="numeric" maxLength={6} value={pinForm.nuevo} data-testid="pin-nuevo-input"
              onChange={(e) => setPinForm({ ...pinForm, nuevo: e.target.value.replace(/\D/g, '') })}
              className="w-full text-center text-2xl tracking-widest border border-zinc-300 rounded-xl py-3" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-zinc-600">Repetir nuevo PIN</label>
            <input type="password" inputMode="numeric" maxLength={6} value={pinForm.repetir} data-testid="pin-repetir-input"
              onChange={(e) => setPinForm({ ...pinForm, repetir: e.target.value.replace(/\D/g, '') })}
              className="w-full text-center text-2xl tracking-widest border border-zinc-300 rounded-xl py-3" />
          </div>
          <Button onClick={cambiarPin} className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="confirmar-cambio-pin-btn">
            Guardar nuevo PIN
          </Button>
        </div>
      </div>
    );
  }

  // ===== MIS FICHAJES =====
  if (pantalla === 'mis_fichajes') {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center p-6 sm:p-10" data-testid="mis-fichajes-screen">
        <button onClick={() => setPantalla('actions')} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-5 w-5" /> Volver
        </button>
        <div className="text-center mb-6 mt-6">
          <FileClock className="h-10 w-10 mx-auto mb-2 text-zinc-700" />
          <h2 className="font-heading text-2xl font-bold text-zinc-900">Mis fichajes</h2>
          <p className="text-zinc-500 text-sm">{seleccionado?.full_name}</p>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-wrap items-end gap-3 mb-4 justify-center">
            <div className="space-y-1">
              <Label>Desde</Label>
              <Input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="mis-desde" className="w-40" />
            </div>
            <div className="space-y-1">
              <Label>Hasta</Label>
              <Input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="mis-hasta" className="w-40" />
            </div>
            <Button onClick={buscarMisFichajes} className="bg-zinc-900 hover:bg-zinc-800" data-testid="buscar-mis-fichajes-btn">
              <Search className="h-4 w-4 mr-1" /> Buscar
            </Button>
          </div>
          {misFichajes && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 flex justify-between text-sm">
                <span className="font-semibold text-zinc-700">{misFichajes.dias.length} jornada(s)</span>
                <span className="text-zinc-500">Total: <b>{formatHoras(misFichajes.total_horas)}</b></span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                  <tr><th className="py-2 px-4 text-left">Fecha</th><th className="py-2 px-4 text-left">Entrada</th><th className="py-2 px-4 text-left">Salida</th><th className="py-2 px-4 text-left">Horas</th></tr>
                </thead>
                <tbody>
                  {misFichajes.dias.map((d, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="py-2 px-4 tabular-nums">{new Date(d.fecha).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}</td>
                      <td className="py-2 px-4 tabular-nums">{d.entrada ? new Date(d.entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '-'}</td>
                      <td className="py-2 px-4 tabular-nums">{d.salida ? new Date(d.salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '-'}</td>
                      <td className="py-2 px-4 tabular-nums font-semibold">{formatHoras(d.horas_trabajadas)}</td>
                    </tr>
                  ))}
                  {misFichajes.dias.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-zinc-400">Sin fichajes en ese periodo.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== SUCCESS =====
  return (
    <div className="min-h-screen bg-green-600 flex flex-col items-center justify-center p-6 text-white" data-testid="success-screen">
      <CheckCircle2 className="h-24 w-24 mb-6" />
      <h2 className="font-heading text-4xl font-bold mb-2 text-center">{exito?.accion}</h2>
      <p className="text-xl opacity-90">{exito?.nombre}</p>
      <p className="text-6xl font-bold tabular-nums mt-6">{exito?.hora}</p>
    </div>
  );
};

export default Kiosk;

// ===== Sub-componente: pantalla de aceptación de cierres automáticos =====
function KioskCierresAckScreen({ cierres, seleccionado, color, onResponder, onVolver }) {
  const [idx, setIdx] = useState(0);
  const [modoRechazo, setModoRechazo] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const actual = cierres[idx];
  if (!actual) return null;

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Madrid',
  }) : '—';
  const fmtFecha = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const enviar = async (aceptar) => {
    if (!aceptar && comentario.trim().length === 0) {
      toast.error('Indica un motivo para rechazar el cierre');
      return;
    }
    setEnviando(true);
    try {
      await onResponder(actual.id, aceptar, comentario.trim());
      // El padre ya elimina de la lista y navega si es el último
      if (idx + 1 < cierres.length) {
        setIdx(idx + 1);
        setModoRechazo(false);
        setComentario('');
      }
    } finally { setEnviando(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col items-center p-6 sm:p-10" data-testid="kiosk-cierre-ack-screen">
      <button onClick={onVolver} className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-900" data-testid="kiosk-cierre-ack-back">
        <ArrowLeft className="h-5 w-5" /> Volver
      </button>
      <div className="text-center mb-6 mt-6">
        <AlertTriangle className="h-10 w-10 mx-auto mb-2 text-amber-500" />
        <h2 className="font-heading text-2xl font-bold text-zinc-900">Confirma tu jornada</h2>
        <p className="text-zinc-500 text-sm max-w-md">
          {cierres.length > 1
            ? `Cierre ${idx + 1} de ${cierres.length}. Cerramos tu jornada porque olvidaste fichar la salida. Debes confirmar o rechazar el cambio.`
            : 'Cerramos tu jornada anterior automáticamente porque olvidaste fichar la salida. Debes confirmar o rechazar el cambio.'}
        </p>
      </div>
      <div className="w-full max-w-md">
        <div className="mb-3 flex items-center gap-3 justify-center">
          <div className="h-12 w-12 rounded-full text-white font-bold text-lg flex items-center justify-center" style={{ backgroundColor: color }}>
            {seleccionado?.iniciales}
          </div>
          <span className="font-semibold text-zinc-800">{seleccionado?.full_name}</span>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3 shadow-sm">
          <div className="flex justify-between"><span className="text-zinc-500 text-sm">Fecha</span><span className="font-semibold" data-testid="kiosk-cierre-ack-fecha">{fmtFecha(actual.fecha)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500 text-sm">Entrada</span><span className="font-semibold tabular-nums">{fmt(actual.hora_entrada)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500 text-sm">Salida asumida</span><span className="font-semibold tabular-nums">{fmt(actual.hora_fin)}</span></div>
          <div className="flex justify-between pt-2 border-t border-zinc-200"><span className="text-zinc-500 text-sm">Horas registradas</span><span className="font-bold text-emerald-600 text-lg" data-testid="kiosk-cierre-ack-horas">{actual.horas} h</span></div>
        </div>

        {!modoRechazo ? (
          <div className="mt-5 flex flex-col gap-2">
            <Button onClick={() => enviar(true)} disabled={enviando} data-testid="kiosk-cierre-ack-aceptar"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base">
              <Check className="h-5 w-5 mr-2" /> Sí, es correcto
            </Button>
            <Button onClick={() => setModoRechazo(true)} disabled={enviando} variant="outline"
              data-testid="kiosk-cierre-ack-rechazar-abrir"
              className="w-full h-12 border-red-300 text-red-700 hover:bg-red-50 text-base">
              <X className="h-5 w-5 mr-2" /> No es correcto
            </Button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <Label className="text-sm font-semibold text-zinc-700">Motivo del rechazo</Label>
            <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3}
              data-testid="kiosk-cierre-ack-comentario"
              placeholder="Explica brevemente qué pasó (ej: seguí trabajando hasta las 20:00)."
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setModoRechazo(false); setComentario(''); }} disabled={enviando} className="flex-1">Cancelar</Button>
              <Button onClick={() => enviar(false)} disabled={enviando || comentario.trim().length === 0}
                data-testid="kiosk-cierre-ack-rechazar-confirm"
                className="flex-1 bg-red-600 hover:bg-red-700">Enviar rechazo</Button>
            </div>
          </div>
        )}

        <p className="text-[11px] text-zinc-400 text-center mt-4">Tu respuesta quedará registrada para que la empresa la revise.</p>
      </div>
    </div>
  );
}

